import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { banquetService } from '../services/systems/banquet/banquet.service';
import { econReportService } from '../services/systems/econ-report/econReport.service';
import { fetchGaisukeArenaLeaderboard } from '../services/systems/gaisuke/gaisuke.service';
import {
  gaisukeFxService,
  type GaisukeFxPublicStatus,
} from '../services/systems/gaisuke/gaisukeFx.service';
import {
  mergeGaisukeArenaScoreboardWithFxLead,
  pickGaisukeFxScoreboardLeadArm,
  sortGaisukeFxArmsByEquity,
} from '../utils/gaisukeArenaScoreboardMerge';
import {
  formatKeibaRatePct,
  mapKeibaCareerPerformanceRow,
} from '../utils/keibaArenaPerformanceStats';
import { parseGaisukeFxMechanicalArm } from '../utils/gaisukeFxArmMeta';
import type { KensukeFeatureCardId } from '../constants/kensukeFeatureCards';

/** カード1枚に添える1行。取得できないコンテンツは key を持たない。 */
export type AiContentHighlights = Partial<Record<KensukeFeatureCardId, string>>;

type Translate = (key: string, options?: object) => string;

const STALE_MS = 5 * 60 * 1000;

/** 公開 API なので落ちても黙って行を出さない（トップの表示を止めない） */
const QUIET = { staleTime: STALE_MS, retry: false, refetchOnWindowFocus: false } as const;

type KeibaPerformanceRow = Parameters<typeof mapKeibaCareerPerformanceRow>[0];

async function fetchKeibaCareerPerformance(): Promise<KeibaPerformanceRow[]> {
  const res = await api.get<{ rows?: KeibaPerformanceRow[] }>('/keiba-arena/public/performance');
  return res.data.rows ?? [];
}

const FX_ARM_LABEL: Record<string, { key: string; fallback: string }> = {
  fusion: { key: 'gaisukeFx.fusion', fallback: '日足融合（Google）' },
  fusion_1h: { key: 'gaisukeFx.fusion_1h', fallback: '1時間融合（Google）' },
  fusion_1wk: { key: 'gaisukeFx.fusion_1wk', fallback: '週足融合（Google）' },
  fusion_openai: { key: 'gaisukeFx.fusion_openai', fallback: '日足融合（OpenAI）' },
  fusion_openai_1h: { key: 'gaisukeFx.fusion_openai_1h', fallback: '1時間融合（OpenAI）' },
  fusion_openai_1wk: { key: 'gaisukeFx.fusion_openai_1wk', fallback: '週足融合（OpenAI）' },
  fusion_anthropic: { key: 'gaisukeFx.fusion_anthropic', fallback: '日足融合（Anthropic）' },
  fusion_anthropic_1h: { key: 'gaisukeFx.fusion_anthropic_1h', fallback: '1時間融合（Anthropic）' },
  fusion_anthropic_1wk: { key: 'gaisukeFx.fusion_anthropic_1wk', fallback: '週足融合（Anthropic）' },
  carry: { key: 'gaisukeFx.carry', fallback: 'キャリー' },
  turtles: { key: 'gaisukeFx.turtles', fallback: 'タートルズ' },
  london: { key: 'gaisukeFx.london', fallback: 'ロンドンブレイク' },
  value: { key: 'gaisukeFx.value', fallback: 'バリュー' },
  legacy_rules: { key: 'gaisukeFx.legacy', fallback: 'テクニカル多数決' },
  best_3d: { key: 'gaisukeFx.best_3d', fallback: '直近3日最優秀' },
  best_1w: { key: 'gaisukeFx.best_1w', fallback: '直近1週最優秀' },
  best_2w: { key: 'gaisukeFx.best_2w', fallback: '直近2週最優秀' },
  best_1m: { key: 'gaisukeFx.best_1m', fallback: '直近1ヶ月最優秀' },
  top7_3d: { key: 'gaisukeFx.top7_3d', fallback: '3日上位7の多数決' },
  top7_1w: { key: 'gaisukeFx.top7_1w', fallback: '1週上位7の多数決' },
  top7_2w: { key: 'gaisukeFx.top7_2w', fallback: '2週上位7の多数決' },
  top7_1m: { key: 'gaisukeFx.top7_1m', fallback: '1ヶ月上位7の多数決' },
  worst_3d: { key: 'gaisukeFx.worst_3d', fallback: '曲がり屋3日' },
  worst_1w: { key: 'gaisukeFx.worst_1w', fallback: '曲がり屋1週' },
  worst_2w: { key: 'gaisukeFx.worst_2w', fallback: '曲がり屋2週' },
  worst_1m: { key: 'gaisukeFx.worst_1m', fallback: '曲がり屋1ヶ月' },
};

function fxStrategyLabel(key: string, t: Translate): string {
  const inv = key.endsWith('_inv') ? key.slice(0, -4) : null;
  const labelKey = inv || key;
  const mech = parseGaisukeFxMechanicalArm(labelKey);
  let name: string;
  if (mech) {
    const base = FX_ARM_LABEL[mech.base];
    const baseName = base ? t(base.key, { defaultValue: base.fallback }) : mech.base;
    const tfFallback =
      mech.tf === '1d' ? '日足' : mech.tf === '1h' ? '1時間' : mech.tf === '4h' ? '4時間' : mech.tf;
    name = t('gaisukeFx.arm_with_tf', {
      defaultValue: '{{name}}（{{tf}}）',
      name: baseName,
      tf: t(`gaisukeFx.tf_${mech.tf}`, { defaultValue: tfFallback }),
    });
  } else {
    const label = FX_ARM_LABEL[labelKey];
    name = label ? t(label.key, { defaultValue: label.fallback }) : labelKey;
  }
  if (!inv) return name;
  return t('gaisukeFx.arm_inverse', { defaultValue: '{{name}}・逆', name });
}

/**
 * 本編カードの見どころ1行。対戦スコアボードと同じく本編3社＋FX総資産1位を損益率で混ぜる。
 * FX 枠が先頭のときは短い「FXドル円 / 方針名」を出す（第4のAIメーカーとは言わない）。
 */
function gaisukeHighlight(
  entries: Awaited<ReturnType<typeof fetchGaisukeArenaLeaderboard>> | undefined,
  fxStatus: GaisukeFxPublicStatus | undefined,
  t: Translate
): string | undefined {
  if (!entries?.length) return undefined;
  const lead = pickGaisukeFxScoreboardLeadArm(fxStatus?.arms);
  const merged = mergeGaisukeArenaScoreboardWithFxLead({
    mainEntries: entries,
    fxStatus,
    fxLabels: lead
      ? { name: fxStrategyLabel(lead.key, t), model: '' }
      : null,
  });
  const top = merged[0];
  if (!top) return undefined;
  const name =
    top.kind === 'fx'
      ? t('home.ai_highlight_gaisuke_fx_lead_name', {
          defaultValue: 'FXドル円 / {{strategy}}',
          strategy: top.name,
        })
      : top.name;
  return t('home.ai_highlight_gaisuke', {
    defaultValue: '累計1位 {{name}} {{pct}}',
    name,
    pct: `${top.returnPct >= 0 ? '+' : ''}${top.returnPct.toFixed(1)}%`,
  });
}

/** 公開カードと同じく現金を除き、総資産が一番多い戦略を1行で出す */
function gaisukeFxHighlight(
  status: GaisukeFxPublicStatus | undefined,
  t: Translate
): string | undefined {
  if (!status || status.status === 'not_started') return undefined;
  const top = sortGaisukeFxArmsByEquity(
    status.arms.filter((a) => a.key !== 'cash' && a.canTrade !== false)
  )[0];
  if (!top) return undefined;
  const name = fxStrategyLabel(top.key, t);
  const pct = `${top.pnlPct >= 0 ? '+' : ''}${top.pnlPct.toFixed(1)}%`;
  const pnlJpy = Math.round(top.cumulativePnlJpy);
  const signedYen =
    pnlJpy === 0
      ? '¥0'
      : `${pnlJpy > 0 ? '+' : '-'}¥${Math.abs(pnlJpy).toLocaleString()}`;
  return t('home.ai_highlight_gaisuke_fx', {
    defaultValue: '1位 {{name}} 損益 {{pnl}}（{{pct}}）',
    name,
    pnl: signedYen,
    pct,
  });
}

/** 競馬アリーナの主指標はレース的中率（仮想資金ではない） */
function keibaHighlight(rows: KeibaPerformanceRow[] | undefined, t: Translate): string | undefined {
  const played = (rows ?? [])
    .map((row) => mapKeibaCareerPerformanceRow(row))
    .filter((row) => row.racePlayed > 0 && row.raceHitRate != null);
  if (played.length === 0) return undefined;
  const top = played.sort((a, b) => (b.raceHitRate ?? 0) - (a.raceHitRate ?? 0))[0];
  return t('home.ai_highlight_keiba_arena', {
    defaultValue: 'レース的中率1位 {{name}} {{pct}}',
    name: top.armName,
    pct: formatKeibaRatePct(top.raceHitRate),
  });
}

function banquetHighlight(
  hub: Awaited<ReturnType<typeof banquetService.getHub>> | undefined,
  t: Translate
): string | undefined {
  const featured = hub?.featured;
  if (!featured?.titleJa) return undefined;
  if (featured.episodeNumber != null) {
    return t('home.ai_highlight_banquet_numbered', {
      defaultValue: '最新回 第{{episode}}回 {{title}}',
      episode: featured.episodeNumber,
      title: featured.titleJa,
    });
  }
  return t('home.ai_highlight_banquet', {
    defaultValue: '最新回 {{title}}',
    title: featured.titleJa,
  });
}

function econReportHighlight(
  hub: Awaited<ReturnType<typeof econReportService.getHub>> | undefined,
  t: Translate
): string | undefined {
  const latest = hub?.reports?.[0];
  if (!latest?.title) return undefined;
  return t('home.ai_highlight_econ_report', {
    defaultValue: '最新レポート {{title}}',
    title: latest.title,
  });
}

/**
 * AIコンテンツ各カードの「今の見どころ」を1行ずつ返す。
 * `enabled` が false のときは一切リクエストしない。
 */
export function useAiContentHighlights(enabled: boolean): AiContentHighlights {
  const { t } = useTranslation();
  const translate = t as Translate;

  const gaisuke = useQuery({
    queryKey: ['ai-highlight', 'gaisuke'],
    queryFn: fetchGaisukeArenaLeaderboard,
    enabled,
    ...QUIET,
  });
  const gaisukeFx = useQuery({
    queryKey: ['ai-highlight', 'gaisuke-fx'],
    queryFn: () => gaisukeFxService.getStatus(),
    enabled,
    ...QUIET,
  });
  const keiba = useQuery({
    queryKey: ['ai-highlight', 'keiba-arena'],
    queryFn: fetchKeibaCareerPerformance,
    enabled,
    ...QUIET,
  });
  const banquet = useQuery({
    queryKey: ['ai-highlight', 'banquet'],
    queryFn: () => banquetService.getHub(),
    enabled,
    ...QUIET,
  });
  const econReport = useQuery({
    queryKey: ['ai-highlight', 'econ-report'],
    queryFn: () => econReportService.getHub(),
    enabled,
    ...QUIET,
  });

  const highlights: AiContentHighlights = {};
  const put = (id: KensukeFeatureCardId, text: string | undefined) => {
    if (text) highlights[id] = text;
  };
  put('gaisuke', gaisukeHighlight(gaisuke.data, gaisukeFx.data, translate));
  put('gaisuke_fx', gaisukeFxHighlight(gaisukeFx.data, translate));
  put('keiba_arena', keibaHighlight(keiba.data, translate));
  put('banquet', banquetHighlight(banquet.data, translate));
  put('econ_report', econReportHighlight(econReport.data, translate));
  return highlights;
}
